import {
  signIn,
  confirmSignIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  type ResetPasswordOutput,
  type ConfirmResetPasswordInput,
} from 'aws-amplify/auth';
import { nomeDoErro } from '@/lib/erroCognito';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface UserInfo {
  username: string;
  email: string;
  name?: string;
  groups: string[];
  idClube?: number;
  nomeClube?: string;
}

const AMPLIFY_SIGNIN_STATE_KEYS = [
  'CognitoSignInState.username',
  'CognitoSignInState.challengeName',
  'CognitoSignInState.signInSession',
  'CognitoSignInState.expiry',
];

// O signOut() do Amplify não limpa esse estado interno de sign-in (signInStore),
// que fica em sessionStorage por até 3min quando um login anterior deixa um
// challenge pendente (ex.: troca de senha obrigatória). Sem isso, o próximo
// login com outro usuário reaproveita o username antigo ao cachear os tokens
// e corrompe a sessão local.
function clearStaleAmplifySignInState(): void {
  AMPLIFY_SIGNIN_STATE_KEYS.forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Conta criada pelo backoffice nasce com senha temporária enviada por e-mail no
 * convite do Cognito; no primeiro login o Cognito exige que o usuário defina a
 * própria senha antes de liberar a sessão.
 */
export type ResultadoLogin = { etapa: 'concluido'; usuario: UserInfo } | { etapa: 'nova-senha' };

function erroDeEtapa(signInStep: string): Error {
  const challengeError = new Error(`Login incompleto: etapa adicional exigida pelo Cognito (${signInStep}).`);
  challengeError.name = 'AuthChallengeError';
  return challengeError;
}

export async function login(credentials: LoginCredentials): Promise<ResultadoLogin> {
  clearStaleAmplifySignInState();
  let result;
  try {
    result = await signIn({ username: credentials.email, password: credentials.password });
  } catch (err: unknown) {
    if (nomeDoErro(err) === 'UserAlreadyAuthenticatedException') {
      await signOut();
      clearStaleAmplifySignInState();
      result = await signIn({ username: credentials.email, password: credentials.password });
    } else {
      throw err;
    }
  }

  if (!result.isSignedIn) {
    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { etapa: 'nova-senha' };
    }
    throw erroDeEtapa(result.nextStep.signInStep);
  }

  return { etapa: 'concluido', usuario: await getCurrentUserInfo() };
}

/** Responde à troca obrigatória de senha do primeiro login (ver `ResultadoLogin`). */
export async function definirNovaSenha(novaSenha: string): Promise<UserInfo> {
  const result = await confirmSignIn({ challengeResponse: novaSenha });
  if (!result.isSignedIn) {
    throw erroDeEtapa(result.nextStep.signInStep);
  }
  return getCurrentUserInfo();
}

export async function logout(): Promise<void> {
  await signOut();
}

export async function getCurrentUserInfo(): Promise<UserInfo> {
  const [user, attributes, session] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes(),
    fetchAuthSession(),
  ]);

  const groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) ?? [];
  const idClubeStr = session.tokens?.accessToken?.payload['id_clube'] as string | undefined;
  const idClube = idClubeStr ? Number.parseInt(idClubeStr, 10) : undefined;

  return {
    username: user.username,
    email: attributes.email ?? user.username,
    name: attributes.name,
    groups,
    idClube: Number.isNaN(idClube) ? undefined : idClube,
  };
}

export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

export interface AuthTokens {
  accessToken: string;
  idToken: string;
}

export async function getAuthTokens(): Promise<AuthTokens> {
  const session = await fetchAuthSession();
  if (!session.tokens) throw new Error('Sessão inválida ou expirada');
  return {
    accessToken: session.tokens.accessToken.toString(),
    idToken: session.tokens.idToken?.toString() ?? '',
  };
}

export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function initiatePasswordReset(email: string): Promise<ResetPasswordOutput> {
  return resetPassword({ username: email });
}

export async function confirmPasswordResetFlow(params: ConfirmResetPasswordInput): Promise<void> {
  await confirmResetPassword(params);
}
